/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'

import type { EuiBasicTableColumn } from '@elastic/eui'
import { EuiFlexGroup, EuiFlexItem, EuiBadge, EuiText, EuiLink } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import Status from '../../components/Status'
import BalanceInfo from '../../components/BalanceInfo'

import type { Alert } from './types'

type Props = {
  onViewAlert: (alert: Alert) => void
}

const useColumns = ({ onViewAlert }: Props): Array<EuiBasicTableColumn<Alert>> => [
  {
    field: 'active',
    name: (
      <FormattedMessage
        id='billing.notifications.prepaid-consumption.table.status-header'
        defaultMessage='Status'
      />
    ),
    dataType: 'boolean',
    render: (active: Alert['active']) => <Status active={active} />,
    width: '100px',
    mobileOptions: { width: '100%' },
  },
  {
    field: 'scope',
    name: (
      <FormattedMessage
        id='billing.notifications.prepaid-consumption.table.scope-header'
        defaultMessage='Scope'
      />
    ),
    render: (scope: Alert['scope']) => <EuiBadge color='hollow'>{scope}</EuiBadge>,
    width: '128px',
    mobileOptions: { width: '100%' },
  },
  {
    field: 'balance',
    name: (
      <FormattedMessage
        id='billing.notifications.prepaid-consumption.table.remaining-header'
        defaultMessage='Current vs Credits (ECU)'
      />
    ),
    render: (balance: Alert['balance']) => (
      <BalanceInfo
        current={Math.max(balance.total - balance.remaining, 0)}
        max={balance.total || 0}
        showPercentage={true}
      />
    ),
    width: '270px',
    mobileOptions: { width: '100%' },
  },

  {
    field: 'triggers',
    name: (
      <FormattedMessage
        id='billing.notifications.prepaid-consumption.table.triggers-header'
        defaultMessage='Notify at'
      />
    ),
    render: (triggers: Alert['triggers']) => (
      <EuiFlexGroup
        gutterSize='xs'
        responsive={false}
        alignItems='center'
        wrap={true}
        css={css`
          white-space: nowrap;
        `}
      >
        {triggers.map((trigger) => (
          <EuiFlexItem key={trigger} grow={false}>
            <EuiBadge color='hollow'>{trigger}</EuiBadge>
          </EuiFlexItem>
        ))}
        <EuiFlexItem>
          <EuiText size='xs'>
            <FormattedMessage
              id='billing.notifications.prepaid-consumption.table.triggers-description'
              defaultMessage='remaining credits'
            />
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
    ),
    width: '140px',
    mobileOptions: { width: '100%' },
  },
  {
    name: (
      <FormattedMessage
        id='billing.notifications.prepaid-consumption.table.actions'
        defaultMessage='Actions'
      />
    ),
    render: () => null,
    width: '140px',
    align: 'right',
    actions: [
      {
        isPrimary: true,
        onClick: onViewAlert,
        render: (alert: Alert) => (
          <EuiLink color='primary' onClick={() => onViewAlert(alert)} type='button'>
            <FormattedMessage
              id='billing.notifications.credit-consumption.alert-details.actions.view'
              defaultMessage='View'
            />
          </EuiLink>
        ),
      },
    ],
    mobileOptions: { width: '100%' },
  },
]

export default useColumns
